/**
 * Detection Rules v2 — Sigma per-type hooks + types.
 *
 * Backing routes: cmd/admin-bff/rules_sigma.go (sibling agent in flight).
 * Typed against the spec in the route map; if the sibling commit lands first
 * and shifts response shapes, adjust here.
 *
 * Query key convention:
 *   ["rules","sigma","list",filters]
 *   ["rules","sigma","detail",id]
 *   ["rules","sigma","convert",id,backend,version]
 *   ["rules","sigma","backends"]
 *   ["rules","sigma","corpora"]
 *   ["rules","sigma","imports"]
 *   ["rules","sigma","imports",id]
 *   ["rules","sigma","logsource-values"]
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";
import type { LintMessage, Rule, RuleSeverity, RuleTLP, RuleVersion } from "./rules";

// ---------- Types ----------

export type SigmaLevel = "informational" | "low" | "medium" | "high" | "critical";
export type SigmaStatus = "stable" | "test" | "experimental" | "deprecated" | "unsupported";

export const SIGMA_LEVELS: ReadonlyArray<{ value: SigmaLevel; label: string }> = [
    { value: "informational", label: "Informational" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
];

export const SIGMA_STATUSES: ReadonlyArray<{ value: SigmaStatus; label: string }> = [
    { value: "stable", label: "Stable" },
    { value: "test", label: "Test" },
    { value: "experimental", label: "Experimental" },
    { value: "deprecated", label: "Deprecated" },
    { value: "unsupported", label: "Unsupported" },
];

/** 14 ATT&CK tactics (enterprise). */
export const ATTACK_TACTICS: ReadonlyArray<{ slug: string; label: string }> = [
    { slug: "reconnaissance", label: "Reconnaissance" },
    { slug: "resource_development", label: "Resource development" },
    { slug: "initial_access", label: "Initial access" },
    { slug: "execution", label: "Execution" },
    { slug: "persistence", label: "Persistence" },
    { slug: "privilege_escalation", label: "Privilege escalation" },
    { slug: "defense_evasion", label: "Defense evasion" },
    { slug: "credential_access", label: "Credential access" },
    { slug: "discovery", label: "Discovery" },
    { slug: "lateral_movement", label: "Lateral movement" },
    { slug: "collection", label: "Collection" },
    { slug: "command_and_control", label: "Command and control" },
    { slug: "exfiltration", label: "Exfiltration" },
    { slug: "impact", label: "Impact" },
];

export const ATTACK_TECHNIQUE_RE = /^attack\.t\d{4}(\.\d{3})?$/;
export const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface SigmaLogsource {
    category: string;
    product: string;
    service: string;
}

export interface SigmaMeta {
    sigmaId?: string;
    sigmaStatus: SigmaStatus;
    logsource: SigmaLogsource;
    level: SigmaLevel;
    references: string[];
    falsepositives: string[];
    fields: string[];
    relatedRules: Array<{ id: string; type: string }>;
    sigmaTags: string[];
    isCorrelation: boolean;
    correlationType?: string;
    supportedBackends: string[];
    author?: string;
    date?: string;
    modified?: string;
}

export interface SigmaListRow {
    id: string;
    slug: string;
    name: string;
    severity: string;
    status: string;
    currentVersion: number;
    publishedVersion?: number;
    tags: string[];
    imported: boolean;
    license?: string;
    updatedAt: string;
    sigmaId?: string;
    logsource: SigmaLogsource;
    level: SigmaLevel;
    sigmaStatus: SigmaStatus;
    sigmaTags: string[];
    supportedBackends: string[];
}

export interface SigmaListFilters {
    q?: string;
    logsourceCategory?: string;
    logsourceProduct?: string;
    logsourceService?: string;
    level?: SigmaLevel | "";
    attackTactic?: string;
    attackTechnique?: string;
    status?: string;
    imported?: boolean;
    limit?: number;
    offset?: number;
}

export interface SigmaConversion {
    backend: string;
    query: string;
    warnings: Array<{ severity: string; code: string; msg: string }>;
    fieldMappings?: Record<string, unknown>;
    cachedAt?: string;
}

export interface SigmaDetail {
    rule: Rule;
    sigmaMeta: SigmaMeta;
    currentVersion: RuleVersion;
    recentDryRuns: Array<{
        id: string;
        version: number;
        corpusSlug?: string;
        status: string;
        matches: number;
        elapsedMs: number;
        startedAt: string;
        engine: string;
    }>;
    cachedConversions: SigmaConversion[];
}

export interface SigmaBackend {
    slug: string;
    displayName: string;
    notes?: string;
}

export interface SigmaCorpus {
    slug: string;
    name: string;
    kind: string;
    description: string;
    itemCount: number;
    objectUrl: string;
}

export interface SigmaImportRow {
    id: string;
    registrySlug: string;
    temporalWorkflowId?: string;
    temporalRunId?: string;
    status: "running" | "ok" | "error" | "cancelled";
    counters: Record<string, unknown>;
    startedAt: string;
    finishedAt?: string;
    elapsedMs: number;
}

export interface SigmaDryRunResult {
    engine: string;
    matches: Array<{ sample: string; rule: string; tags: string[]; strings?: string[] }>;
    total: number;
    elapsedMs: number;
    warnings?: string[];
}

// ---------- Queries ----------

function toQuery(filters: SigmaListFilters): string {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null || v === "") continue;
        p.set(k, String(v));
    }
    return p.toString();
}

export function useSigmaList(filters: SigmaListFilters = {}) {
    return useQuery({
        queryKey: ["rules", "sigma", "list", filters] as const,
        queryFn: () =>
            api.get<{ rules: SigmaListRow[]; total: number }>(
                `/api/v2/rules/sigma?${toQuery(filters)}`,
            ),
    });
}

export function useSigmaDetail(id: string | undefined) {
    return useQuery({
        queryKey: ["rules", "sigma", "detail", id] as const,
        queryFn: () => api.get<SigmaDetail>(`/api/v2/rules/sigma/r/${id}`),
        enabled: !!id,
    });
}

export function useSigmaDryRuns(id: string | undefined) {
    return useQuery({
        queryKey: ["rules", "sigma", "dry-runs", id] as const,
        queryFn: () =>
            api.get<{ dryRuns: SigmaDetail["recentDryRuns"] }>(
                `/api/v2/rules/sigma/r/${id}/dry-runs`,
            ),
        enabled: !!id,
    });
}

export function useSigmaBackends() {
    return useQuery({
        queryKey: ["rules", "sigma", "backends"] as const,
        queryFn: () =>
            api
                .get<{ backends: SigmaBackend[] }>(`/api/v2/rules/sigma/backends`)
                .then((d) => d.backends ?? []),
        staleTime: 5 * 60_000,
    });
}

export function useSigmaCorpora() {
    return useQuery({
        queryKey: ["rules", "sigma", "corpora"] as const,
        queryFn: () =>
            api
                .get<{ corpora: SigmaCorpus[] }>(`/api/v2/rules/sigma/corpora`)
                .then((d) => d.corpora ?? []),
        staleTime: 60_000,
    });
}

export function useSigmaImports() {
    return useQuery({
        queryKey: ["rules", "sigma", "imports"] as const,
        queryFn: () =>
            api
                .get<{ imports: SigmaImportRow[] }>(`/api/v2/rules/sigma/imports`)
                .then((d) => d.imports ?? []),
        refetchInterval: 5_000,
    });
}

export function useSigmaImport(id: string | undefined) {
    return useQuery({
        queryKey: ["rules", "sigma", "imports", id] as const,
        queryFn: () =>
            api
                .get<{ import: SigmaImportRow }>(`/api/v2/rules/sigma/imports/${id}`)
                .then((d) => d.import),
        enabled: !!id,
        refetchInterval: 3_000,
    });
}

export function useConvert(
    id: string | undefined,
    backend: string | undefined,
    version?: number,
    pipeline?: string,
) {
    const qs = new URLSearchParams();
    if (backend) qs.set("backend", backend);
    if (version) qs.set("version", String(version));
    if (pipeline) qs.set("pipeline", pipeline);
    return useQuery({
        queryKey: ["rules", "sigma", "convert", id, backend, version ?? null] as const,
        // Handler returns the conversion fields flat (not under a `conversion`
        // envelope). Aligned with cmd/admin-bff/rules_sigma.go:769.
        queryFn: () =>
            api.get<SigmaConversion & { version: number }>(
                `/api/v2/rules/sigma/r/${id}/convert?${qs.toString()}`,
            ),
        enabled: !!id && !!backend,
        staleTime: 30_000,
    });
}

/**
 * Distinct logsource values derived from the list endpoint. Caches client-
 * side; refreshes when the list query refreshes.
 */
export function useLogsourceValues() {
    const q = useSigmaList({ limit: 200 });
    return useMemo(() => {
        const cats = new Set<string>();
        const prods = new Set<string>();
        const svcs = new Set<string>();
        for (const r of q.data?.rules ?? []) {
            if (r.logsource?.category) cats.add(r.logsource.category);
            if (r.logsource?.product) prods.add(r.logsource.product);
            if (r.logsource?.service) svcs.add(r.logsource.service);
        }
        return {
            categories: [...cats].sort(),
            products: [...prods].sort(),
            services: [...svcs].sort(),
            isLoading: q.isLoading,
        };
    }, [q.data, q.isLoading]);
}

// ---------- Mutations ----------

export interface CreateSigmaInput {
    slug?: string;
    name: string;
    description: string;
    severity: RuleSeverity;
    tlp: RuleTLP;
    tags: string[];
    content: string;
    meta: {
        sigmaId?: string;
        level: SigmaLevel;
        sigmaStatus: SigmaStatus;
        logsource: SigmaLogsource;
        author?: string;
        references?: string[];
        falsepositives?: string[];
        fields?: string[];
        sigmaTags?: string[];
    };
}

export function useCreateSigma() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateSigmaInput) =>
            api.post<{ rule: Rule; sigmaMeta: SigmaMeta; lintMessages: LintMessage[] }>(
                `/api/v2/rules/sigma`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "list"] });
        },
    });
}

/**
 * Patch shape mirrors the admin-bff handler in rules_sigma.go:patchMeta —
 * logsource is sent as three flat fields (logsourceCategory / Product /
 * Service) rather than a nested object, and the handler accepts a small
 * subset of meta fields (no author / date / modified — those flow via
 * content edits, parsed from the YAML).
 */
export interface PatchSigmaMetaInput {
    level?: SigmaLevel;
    sigmaStatus?: SigmaStatus;
    logsourceCategory?: string;
    logsourceProduct?: string;
    logsourceService?: string;
    references?: string[];
    falsepositives?: string[];
    fields?: string[];
    sigmaTags?: string[];
    supportedBackends?: string[];
    isCorrelation?: boolean;
    correlationType?: string;
}

export function usePatchSigmaMeta(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: PatchSigmaMetaInput) =>
            api.patch<{ sigmaMeta: SigmaMeta }>(`/api/v2/rules/sigma/r/${id}`, patch),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "list"] });
        },
    });
}

export function usePutSigmaContent(id: string) {
    const qc = useQueryClient();
    return useMutation({
        // Handler in rules_sigma.go:saveContent also returns the refreshed
        // sigmaMeta (parsed from the YAML on save) — surface it so callers
        // can refresh local state without a separate detail re-fetch.
        mutationFn: (input: { content: string; note?: string }) =>
            api.put<{ version: RuleVersion; sigmaMeta: SigmaMeta; lintMessages: LintMessage[] }>(
                `/api/v2/rules/sigma/r/${id}/content`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "list"] });
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "convert", id] });
        },
    });
}

export function useLintSigma(id: string | undefined) {
    return useMutation({
        mutationFn: (input: { content: string }) =>
            api.post<{ lintMessages: LintMessage[] }>(
                `/api/v2/rules/sigma/r/${id}/lint`,
                input,
            ),
    });
}

export function useDryRunSigma(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: {
            corpusSlug: string;
            backend?: string;
            maxSamples?: number;
            timeoutSec?: number;
            version?: number;
        }) =>
            api.post<{ result: SigmaDryRunResult }>(`/api/v2/rules/sigma/r/${id}/dry-run`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "dry-runs", id] });
        },
    });
}

export function useStartSigmaImport() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { registrySlug: string }) =>
            api.post<{ importId: string; temporalWorkflowId: string; status: string; startedAt: string }>(
                `/api/v2/rules/sigma/imports`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "sigma", "imports"] });
        },
    });
}

// ---------- Constants ----------

export const SIGMA_REGISTRIES: ReadonlyArray<{
    slug: string;
    name: string;
    license: string;
    description: string;
}> = [
    {
        slug: "sigmahq/sigma",
        name: "SigmaHQ / sigma",
        license: "DRL-1.1",
        description:
            "Canonical SigmaHQ rules — ~3,000 detections covering Windows, Linux, macOS, cloud, web. Detection Rule License 1.1.",
    },
];

/**
 * Map a Sigma backend slug to a Monaco language id. We register
 * `splunk-spl` lazily; everything else falls back to a sensible built-in.
 */
export function backendLanguage(slug: string): string {
    const s = slug.toLowerCase();
    if (s.includes("kusto") || s.includes("azure") || s.includes("sentinel")) return "kusto";
    if (s.includes("elastic") || s.includes("eql") || s.includes("lucene")) return "kusto";
    if (s.includes("splunk")) return "splunk-spl";
    if (s.includes("sql")) return "sql";
    if (s.includes("powershell")) return "powershell";
    return "plaintext";
}

/** Generate a UUID v4 (browser crypto). */
export function generateUUIDv4(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // Fallback for older runtimes (won't fire under Vite dev/Chrome target).
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
